import React, { useState, useEffect } from 'react';
import Navigation from '../Navbar/Navbar';
import axios from 'axios';
import { Container, Row, Col, Card, Badge, Alert, Spinner, Table, ProgressBar, Button, Modal } from 'react-bootstrap';
import {
    BoxSeam,
    CheckCircle,
    ExclamationTriangle,
    PeopleFill,
    GraphUp,
    ClockHistory,
    Bell,
    Laptop,
    GeoAlt,
    CurrencyDollar
} from 'react-bootstrap-icons';
import {
    PieChart, Pie, Cell,
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
    Legend
} from 'recharts';
import './Dashboard.css';

const apiUrl = "/api";

// Color palette for charts
const COLORS = ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336', '#607D8B'];
const STATUS_COLORS = {
    available: '#4CAF50',
    deployed: '#2196F3',
    in_repair: '#FF9800',
    retired: '#9C27B0',
    lost: '#F44336',
    reserved: '#607D8B',
    // Capitalized statuses from backend
    Deployed: '#2196F3',
    Received: '#9C27B0',       // Purple for Received
    InPrep: '#FFC107',         // Amber for In Preperation
    ReadyForDispatch: '#00BCD4' // Cyan for Ready to Dispatch
};

function Dashboard() {
    const [stats, setStats] = useState(null);
    const [categories, setCategories] = useState([]);
    const [locations, setLocations] = useState([]);
    const [recentActivity, setRecentActivity] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Approval Modal State
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [selectedSignoutId, setSelectedSignoutId] = useState(null);
    const [approvalDetails, setApprovalDetails] = useState(null);
    const [approvalLoading, setApprovalLoading] = useState(false);

    useEffect(() => {
        if (!localStorage.getItem('token')) {
            window.location.href = '#/login';
            return;
        }
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const tenantId = parseInt(localStorage.getItem('selectedTenant')) || 1;
            const headers = { Authorization: `Bearer ${token}` };

            const [statsRes, categoriesRes, locationsRes, activityRes, alertsRes] = await Promise.all([
                axios.get(`${apiUrl}/dashboard/stats?tenant_id=${tenantId}`, { headers }),
                axios.get(`${apiUrl}/dashboard/by-category?tenant_id=${tenantId}`, { headers }),
                axios.get(`${apiUrl}/dashboard/by-location?tenant_id=${tenantId}`, { headers }),
                axios.get(`${apiUrl}/dashboard/recent-activity?tenant_id=${tenantId}&limit=5`, { headers }),
                axios.get(`${apiUrl}/dashboard/alerts?tenant_id=${tenantId}`, { headers })
            ]);

            setStats(statsRes.data);
            setCategories(categoriesRes.data);
            setLocations(locationsRes.data);
            setRecentActivity(activityRes.data);
            setAlerts(alertsRes.data);
            setError(null);
        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            setError('Failed to load dashboard data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenApproval = async (signoutId) => {
        setSelectedSignoutId(signoutId);
        setShowApprovalModal(true);
        setApprovalLoading(true);
        try {
            const token = localStorage.getItem('token');
            const tenantId = parseInt(localStorage.getItem('selectedTenant')) || 1;
            const response = await axios.get(`${apiUrl}/inv/signout/?id=${signoutId}&tenant_id=${tenantId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            // API returns array, we want the first item for details and the rest for list if structure implies
            // Actually existing API returns row per item.
            setApprovalDetails(response.data);
        } catch (err) {
            console.error('Error fetching signout details:', err);
            window.alert('Failed to load signout details.');
            setShowApprovalModal(false);
        } finally {
            setApprovalLoading(false);
        }
    };

    const handleConfirmApproval = async () => {
        try {
            const token = localStorage.getItem('token');
            const tenantId = parseInt(localStorage.getItem('selectedTenant')) || 1;
            await axios.post(`${apiUrl}/signout/approve`, {
                id: selectedSignoutId,
                tenant_id: tenantId
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setShowApprovalModal(false);
            fetchDashboardData(); // Refresh dashboard to remove alert
        } catch (err) {
            console.error('Error approving signout:', err);
            window.alert('Failed to approve signout.');
        }
    };

    // Prepare chart data
    const statusChartData = stats ? Object.entries(stats.byStatus)
        .filter(([_, count]) => count > 0)
        .map(([status, count]) => ({
            name: status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' '),
            value: count,
            color: STATUS_COLORS[status] || '#999'
        })) : [];

    const categoryChartData = categories.slice(0, 8).map((cat, index) => ({
        name: cat.category,
        total: parseInt(cat.total),
        available: parseInt(cat.available),
        deployed: parseInt(cat.deployed)
    }));

    if (loading) {
        return (
            <div>
                <Navigation />
                <Container className="mt-4">
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3">Loading dashboard...</p>
                    </div>
                </Container>
            </div>
        );
    }

    // Helper to resolve static equipment names (Consumables)
    const getEquipmentName = (itemId) => {
        switch (parseInt(itemId)) {
            case 100001: return 'Mouse';
            case 100002: return 'Keyboard';
            case 100003: return 'Ethernet Cable';
            case 100004: return 'USB A-B Cable';
            case 100005: return 'USB Headset';
            case 100006: return 'VGA Cable / Display Cable';
            case 100007: return 'DVI Cable / Display Cable';
            case 100008: return 'Power Cable';
            case 100009: return 'Monitor';
            case 100010: return 'DisplayPort Cable';
            case 100011: return "PC's Power Cable";
            default: return 'Accessory';
        }
    };

    return (
        <div>
            <Navigation />
            <Container className="dashboard-container page-content-wrapper wide py-4">
                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* KPI Cards - Compact Row */}
                <Row className="mb-4 g-3">
                    <Col md={3} sm={6}>
                        <Card className="kpi-card compact">
                            <Card.Body className="d-flex align-items-center justify-content-between p-3">
                                <div>
                                    <p className="kpi-label mb-1">Total Assets</p>
                                    <h3 className="kpi-value mb-0">{stats?.totalAssets || 0}</h3>
                                </div>
                                <div className="kpi-icon-wrapper bg-light text-primary rounded-circle p-2">
                                    <BoxSeam size={20} />
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3} sm={6}>
                        <Card className="kpi-card compact">
                            <Card.Body className="d-flex align-items-center justify-content-between p-3">
                                <div>
                                    <p className="kpi-label mb-1">Available PCs + Laptops</p>
                                    <h3 className="kpi-value mb-0">
                                        {categories
                                            .filter(cat => ['Laptop', 'Desktop', 'Computer'].includes(cat.category))
                                            .reduce((sum, cat) => sum + (parseInt(cat.available) || 0), 0)
                                        }
                                    </h3>
                                </div>
                                <div className="kpi-icon-wrapper bg-light text-success rounded-circle p-2">
                                    <CheckCircle size={20} />
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3} sm={6}>
                        <Card className="kpi-card compact">
                            <Card.Body className="d-flex align-items-center justify-content-between p-3">
                                <div>
                                    <p className="kpi-label mb-1">Active Signouts</p>
                                    <h3 className="kpi-value mb-0">{stats?.activeSignouts || 0}</h3>
                                </div>
                                <div className="kpi-icon-wrapper bg-light text-info rounded-circle p-2">
                                    <ClockHistory size={20} />
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3} sm={6}>
                        <Card className="kpi-card compact">
                            <Card.Body className="d-flex align-items-center justify-content-between p-3">
                                <div>
                                    <p className="kpi-label mb-1">Active Staff</p>
                                    <h3 className="kpi-value mb-0">{stats?.staffCount || 0}</h3>
                                </div>
                                <div className="kpi-icon-wrapper bg-light text-warning rounded-circle p-2">
                                    <PeopleFill size={20} />
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* KPI Cards - Valuation Row Removed */}

                {/* Main Content Area */}
                <Row className="mb-4">
                    {/* Status Distribution - 50% Width */}
                    <Col lg={6} className="mb-4 mb-lg-0">
                        <Card className="chart-card h-100 border-0 shadow-sm">
                            <Card.Header className="dashboard-card-header border-0 pt-3 px-4 pb-2">
                                <h5 className="mb-0 fw-bold">
                                    <GraphUp className="me-2" />
                                    Asset Status Distribution
                                </h5>
                            </Card.Header>
                            <Card.Body className="d-flex align-items-center justify-content-center p-4">
                                {statusChartData.length > 0 ? (
                                    <div className="w-100 d-flex align-items-center" style={{ minHeight: '350px' }}>
                                        <ResponsiveContainer width="100%" height={350}>
                                            <PieChart>
                                                <Pie
                                                    data={statusChartData}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={80}
                                                    outerRadius={120}
                                                    paddingAngle={3}
                                                    dataKey="value"
                                                    label={({ name, percent, value }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                                                >
                                                    {statusChartData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.color} strokeWidth={1} />
                                                    ))}
                                                </Pie>
                                                <Tooltip
                                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                                />
                                                <Legend verticalAlign="bottom" height={36} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                ) : (
                                    <div className="text-center text-muted py-5">No data available</div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Right Column: Combined Alerts & Activity - 50% Width */}
                    <Col lg={6}>
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Header className="dashboard-card-header border-0 pt-3 px-3">
                                <h6 className="mb-0 fw-bold"><Bell className="me-2" />Activity & Alerts</h6>
                            </Card.Header>
                            <Card.Body className="p-0">
                                <div className="p-3" style={{ maxHeight: '500px', overflowY: 'auto' }}>

                                    {/* Section: Actionable Alerts */}
                                    {alerts.length > 0 && (
                                        <div className="mb-4">
                                            <h6 className="text-muted text-uppercase small fw-bold mb-3 ps-1">
                                                Requires Attention <Badge bg="danger" pill className="ms-1">{alerts.length}</Badge>
                                            </h6>
                                            {alerts.map((alert, index) => (
                                                <div key={`alert-${index}`}
                                                    className={`alert-item shadow-sm p-3 mb-2 rounded bg-white border-start border-4 border-${alert.severity === 'warning' ? 'warning' : 'danger'}`}
                                                    style={{ border: '1px solid #f0f0f0' }}
                                                >
                                                    <div className="d-flex align-items-start">
                                                        <ExclamationTriangle className={`text-${alert.severity === 'warning' ? 'warning' : 'danger'} me-2 mt-1`} />
                                                        <div className="w-100">
                                                            <strong className="d-block text-dark mb-1">{alert.title}</strong>
                                                            <div className="small text-muted mb-2">{alert.message}</div>

                                                            {alert.action === 'approve' && (
                                                                <Button size="sm" variant="success" className="w-100 mt-1" onClick={() => handleOpenApproval(alert.signoutId)}>
                                                                    Check & Approve
                                                                </Button>
                                                            )}
                                                            {/* Link for other alerts */}
                                                            {alert.link && (
                                                                <a href={`#${alert.link}`} className="btn btn-sm btn-outline-primary w-100 mt-1">View Details</a>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Section: Recent Activity History */}
                                    <div>
                                        <h6 className="text-muted text-uppercase small fw-bold mb-3 ps-1">Recent History</h6>
                                        {recentActivity.length > 0 ? (
                                            <ul className="list-unstyled mb-0 ms-1">
                                                {recentActivity.map((activity, index) => (
                                                    <li key={`act-${index}`} className="position-relative pb-3 ps-4 border-start border-2 border-light activity-item">
                                                        <div className="position-absolute top-0 start-0 translate-middle bg-white p-1" style={{ marginTop: '0px' }}>
                                                            <div className="rounded-circle bg-light border" style={{ width: '8px', height: '8px' }}></div>
                                                        </div>
                                                        <div className="text-muted extra-small mb-1 fw-bold" style={{ fontSize: '0.7rem' }}>
                                                            {new Date(activity.created_at).toLocaleDateString()}
                                                            <span className="mx-1">•</span>
                                                            {new Date(activity.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        </div>
                                                        <div className="small text-dark">
                                                            {activity.description || `${activity.action_type} ${activity.entity_type}`}
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <div className="text-center text-muted small py-3">No recent history</div>
                                        )}
                                    </div>

                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Bottom Row: Category Inventory Table - Full Width */}
                <Row className="mb-4">
                    <Col>
                        <Card className="border-0 shadow-sm">
                            {/* Removed Green Header as requested */}
                            <Card.Body className="p-0">
                                <Table hover className="category-table mb-0 align-middle">
                                    <thead className="table-header-theme">
                                        <tr>
                                            <th className="px-4 py-3">Category</th>
                                            <th className="px-4 py-3 text-center">Total</th>
                                            <th className="px-4 py-3 text-center">Available</th>
                                            <th className="px-4 py-3 text-center">Deployed</th>
                                            <th className="px-4 py-3 text-center">In Repair</th>
                                            <th className="px-4 py-3">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {categories.map((cat, index) => (
                                            <tr key={index}>
                                                <td className="px-4 py-3">
                                                    <div className="fw-semibold text-dark">{cat.category}</div>
                                                </td>
                                                <td className="text-center px-4 py-3 text-secondary">{cat.total}</td>
                                                <td className="text-center px-4 py-3">
                                                    <span className="badge bg-soft-success text-success rounded-pill px-3">{cat.available}</span>
                                                </td>
                                                <td className="text-center px-4 py-3">
                                                    <span className="badge bg-soft-primary text-primary rounded-pill px-3">{cat.deployed}</span>
                                                </td>
                                                <td className="text-center px-4 py-3">
                                                    {parseInt(cat.in_repair) > 0 ? (
                                                        <span className="badge bg-soft-warning text-warning rounded-pill px-3">{cat.in_repair}</span>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3">
                                                    {cat.lowStock ? (
                                                        <span className="text-danger small fw-semibold">
                                                            <ExclamationTriangle className="me-1" /> Low Stock
                                                        </span>
                                                    ) : (
                                                        <span className="text-success small fw-semibold">
                                                            <CheckCircle className="me-1" /> OK
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Supervisor Approval Modal */}
            <Modal show={showApprovalModal} onHide={() => setShowApprovalModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Approve Signout Request</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center mb-4">
                        <h5>You're about to approve a sign out for this agent.</h5>
                        <p className="text-muted">Please review the details below before approving.</p>
                    </div>

                    {approvalLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : approvalDetails && approvalDetails.length > 0 ? (
                        <div>
                            <div className="d-flex justify-content-between mb-3 p-3 bg-light rounded">
                                <div><strong>Agent:</strong> {approvalDetails[0].name}</div>
                                <div><strong>Signout ID:</strong> #{selectedSignoutId}</div>
                                <div><strong>Date:</strong> {new Date(approvalDetails[0].date).toLocaleDateString()}</div>
                            </div>

                            <div className="table-responsive">
                                <Table size="sm" bordered hover>
                                    <thead className="bg-light">
                                        <tr>
                                            <th>Item Type</th>
                                            <th>Model</th>
                                            <th>Serial / QR</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {approvalDetails.filter(d => d.itemid).map((item, idx) => (
                                            <tr key={idx}>
                                                <td>{item.Type || getEquipmentName(item.itemid)}</td>
                                                <td>{item.Model || '-'}</td>
                                                <td>{item.Serial || item.qrid || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    ) : (
                        <Alert variant="warning">No details found for this signout.</Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowApprovalModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="success" onClick={handleConfirmApproval} disabled={approvalLoading}>
                        Approve Signout
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default Dashboard;
